import { useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/personnel-report/PersonnelReportAction";
import * as uiHelpers from "./PersonnelReportUIHelpers";
import {
  ExportButton, 
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { PersonnelReportFilter } from "./personnel-report-filter/PersonnelReportFilter";
import { PersonnelReportTable } from "./personnel-report-table/PersonnelReportTable";
import { usePersonnelReportUIContext } from "./PersonnelReportUIContext";
import { useSubheader } from "../../../../../_metronic/layout";

export function PersonnelReportCard() {
  // Personnel Report UI Context
  const personnelReportUIContext = usePersonnelReportUIContext();
  const personnelReportUIProps = useMemo(() => {
    return {
      queryParams: personnelReportUIContext.queryParams
    };
  }, [personnelReportUIContext]);
    
  // Getting curret state of Personnel Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.personnelReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;
  
  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // ...
  const [columns, setColumns] = useState(uiHelpers.initialColumns);
  const tableColumns = columns;

  // Personnel Report Redux state
  const dispatch = useDispatch();

  // Subheader
  const subheader = useSubheader();
  subheader.setTitle("Personel Raporu");

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Personel Raporunu Excele Aktarır */
  const exportPersonnelReportToExcel = () => {
    dispatch(actions.getAllPersonnelReport(personnelReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = tableColumns.filter(column => !column.hidden).map(column => column.width);
      const columns = tableColumns.filter(column => !column.hidden).map(column => column.text);
      const fields = tableColumns.filter(column => !column.hidden).map(column => column.dataField);

      entities.forEach(entity => {
        const row = [];

        fields.forEach(field => {
          const propArr = field.split('.');
          if (propArr.length === 1) row.push(entity[propArr[0]]);
          else if (propArr.length === 2) row.push(entity[propArr[0]][propArr[1]]);
        });
        
        rows.push(row);
      })
      
      const filename = personnelReportUIProps.queryParams.term + " - " + (personnelReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Raporu";
      const sheetname = "Personel Raporu";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Personel Raporunu PDF e Aktarır */
  const exportPersonnelReportToPDF = () => {
    dispatch(actions.getAllPersonnelReport(personnelReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [];
      const columns = tableColumns.filter(column => !column.hidden);

      columns.forEach(column => {
        const propArr = column.dataField.split('.');
        const view = {
          header: column.text,
          rows: entities.map(entity => {
            if (propArr.length === 1) return entity[propArr[0]];
            else if (propArr.length === 2) return entity[propArr[0]][propArr[1]];
          })
        };
        views.push(view);
      });
  
      const filename = personnelReportUIProps.queryParams.term + " - " + (personnelReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Personel Raporunu Yazdırma Penceresini Açar */
  const printPersonnelReport = () => {
    dispatch(actions.getAllPersonnelReport(personnelReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = personnelReportUIProps.queryParams.term + " - " + (personnelReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Raporu";
      const columns = tableColumns.filter(column => !column.hidden);
      const properties = columns.map(column => column.text);
  
      entities.forEach(entity => {
        const row = {};

        columns.forEach(column => {
          const propArr = column.dataField.split('.');
          if (propArr.length === 1) row[column.text] = entity[propArr[0]] ?? "";
          else if (propArr.length === 2) row[column.text] = entity[propArr[0]][propArr[1]] ?? "";
        });

        data.push(row);
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Personel Raporunu Panoya Kopyalar */
  const copyPersonnelReport = () => {
    dispatch(actions.getAllPersonnelReport(personnelReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = tableColumns.filter(column => !column.hidden).map(column => column.text);
      const fields = tableColumns.filter(column => !column.hidden).map(column => column.dataField);
      const header = personnelReportUIProps.queryParams.term + " - " + (personnelReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Raporu";
      
      entities.forEach(entity => {
        const row = [];

        fields.forEach(field => {
          const propArr = field.split('.');
          if (propArr.length === 1) row.push(entity[propArr[0]] ?? "");
          else if (propArr.length === 2) row.push(entity[propArr[0]][propArr[1]] ?? "");
        });

        rows.push(row);
      });

      copy({ header, columns, rows });
    });
  }
  
  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Personel Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>            
            <ExportButton
              exportToExcel={exportPersonnelReportToExcel}
              exportToPDF={exportPersonnelReportToPDF}
              print={printPersonnelReport}
              copy={copyPersonnelReport}
            />
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <PersonnelReportFilter />
        <PersonnelReportTable
          columns={columns}
          setColumns={setColumns}
        />
      </CardBody>
    </Card>
  );
}